import { Flex, Box } from '@chakra-ui/react'
import React, { FC, useEffect, useRef, useState } from 'react'
import MultiCarousel, { CarouselProps, DotProps } from 'react-multi-carousel'
import { CarouselArrow } from '../CarouselArrow'

if (globalThis.window === globalThis) {
  require('react-multi-carousel/lib/styles.css');
  require('./carousel.css');
}

const MEDIA_QUERY_WRITEVALUE = 1399

const CustomDot: FC<DotProps> = ({
  active
}) => {
  return (
    <div
      className={active ? 'custom-dot--active' : 'custom-dot'}
    />
  )
}

type ArrowProps = {
  onClick: () => void
  isDisabled: boolean
}

const LeftArrow: FC<ArrowProps> = ({ onClick, isDisabled }) => (
  <CarouselArrow
    className="carouselPrevious"
    direction="left"
    isDisabled={isDisabled}
    onClick={onClick}
    transform="translateX(-20px)"
  />
)

const RightArrow: FC<ArrowProps> = ({ onClick, isDisabled }) => (
  <CarouselArrow
    className="carouselNext"
    direction="right"
    isDisabled={isDisabled}
    onClick={onClick}
    transform="translateX(20px)"
  />
)

interface ICarouselProps extends Omit<CarouselProps, 'responsive'> {
  slidesToShow?: number,
}

const Carousel: FC<ICarouselProps> = ({ children, slidesToShow = 1, showDots = false, ...rest }) => {

  const carouselRef = useRef<MultiCarousel>(null)

  const totalPagesNumber = React.Children.count(children)

  const [currentIndex, setCurrentIndex] = useState<number>(0)

  const calculateInitialSlidesToShow = () => {
    if (slidesToShow === 1) {
      return 1
    }

    if (window.outerWidth > MEDIA_QUERY_WRITEVALUE) {
      return slidesToShow
    } else {
      return slidesToShow - 1
    }
  }

  const [currentSlidesToShow, setCurrentSlidesToShow] = useState<number>(calculateInitialSlidesToShow())

  const isFirstPage = currentIndex === 0
  const isLastPage = totalPagesNumber - currentIndex <= currentSlidesToShow

  const handleBeforeChange = (next: number) => {
    setCurrentIndex(next)
  }

  useEffect(() => {
    if (slidesToShow === 1) {
      return
    }

    const handleWindowResize = () => {
      if (window.outerWidth > MEDIA_QUERY_WRITEVALUE) {
        setCurrentSlidesToShow(slidesToShow)
        if (currentIndex + slidesToShow > totalPagesNumber) {
          carouselRef.current?.goToSlide(currentIndex - 1)
        }
      } else {
        setCurrentSlidesToShow(slidesToShow - 1)
      }
    }

    window.addEventListener('resize', handleWindowResize)

    return () => {
      window.removeEventListener('resize', handleWindowResize)
    }
  }, [currentIndex, slidesToShow, totalPagesNumber])

  const responsive = {
    desktop: {
      breakpoint: { max: 4000, min: 1400 },
      items: slidesToShow,
      slidesToSlide: slidesToShow,
    },
    reduced: {
      breakpoint: { max: MEDIA_QUERY_WRITEVALUE, min: 1200 },
      items: slidesToShow === 1 ? slidesToShow : slidesToShow - 1,
      slidesToSlide: slidesToShow - 1,
    },
    mobile: {
      breakpoint: { max: 1199, min: 0 },
      items: slidesToShow === 1 ? slidesToShow : slidesToShow - 1,
      slidesToSlide: slidesToShow - 1,
    }
  };

  return (
    <Flex
      className="carouselWrapper"
      display="block"
      position="relative"
      width='full'>
      <MultiCarousel
        beforeChange={handleBeforeChange}
        className="carousel"
        customDot={<CustomDot />}
        customLeftArrow={<LeftArrow isDisabled={isFirstPage} onClick={() => { }} />}
        customRightArrow={<RightArrow isDisabled={isLastPage} onClick={() => { }} />}
        dotListClass='dot-list'
        draggable={false}
        ref={carouselRef}
        renderArrowsWhenDisabled={totalPagesNumber > currentSlidesToShow}
        responsive={responsive}
        showDots={showDots ? totalPagesNumber > currentSlidesToShow : false}
        {...rest}>
        {React.Children.map(children, (child) => {
          return (
            <Box margin={3}>
              {child}
            </Box>
          )
        })}
      </MultiCarousel>
    </Flex>
  )
}

export { Carousel }
